import classNames from "classnames";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineCheckCircle,
  MdOutlineDownload,
  MdOutlineError,
  MdOutlineExplore,
  MdOutlineRefresh,
} from "react-icons/md";
import Button from "shared/components/Button";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import * as XLSX from "xlsx";
import { autoFitColumns, resolveError } from "./constants";
import { CSV_TEMPLATE_HEADERS as DOCS_HEADERS } from "./Docs/constants";
import { CSV_TEMPLATE_HEADERS as PARAMS_HEADERS } from "./Params/constants";
import StepCard from "./StepCard";

const downloadParamsTemplate = (insurancePlans) => {
  const dataRows = insurancePlans.map((plan) => {
    const row = new Array(PARAMS_HEADERS.length).fill("");
    row[0] = plan.identifier || "";
    return row;
  });
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet([PARAMS_HEADERS, ...dataRows]);
  autoFitColumns(worksheet, PARAMS_HEADERS);
  XLSX.utils.book_append_sheet(workbook, worksheet, "Paramètres");
  XLSX.writeFile(workbook, "insurance-plans-params-template.xlsx");
};

const downloadDocsTemplate = (insurancePlans) => {
  const dataRows = insurancePlans.map((plan) => [
    plan.identifier || "",
    "",
    "VRAI",
    "VRAI",
    "FAUX",
  ]);
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet([DOCS_HEADERS, ...dataRows]);
  autoFitColumns(worksheet, DOCS_HEADERS);
  XLSX.utils.book_append_sheet(workbook, worksheet, "Documents");
  XLSX.writeFile(workbook, "insurance-plans-docs-template.xlsx");
};

const StepResult = ({ result, onFinish, onReset }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const IMPORT = t("IMPORT", { returnObjects: true });

  const success = get(result, "success", false);
  const errors = get(result, "errors", []);
  const created = get(result, "created", 0);
  const updated = get(result, "updated", 0);
  const skipped = get(result, "skipped", 0);
  const totalRows = get(result, "totalRows", 0);
  const strategy = get(result, "strategy", "");
  const insurancePlans = get(result, "insurancePlans", []);

  const resultActions = (
    <>
      <Button type="primary--outlined" onClick={onReset}>
        <MdOutlineRefresh size={20} />
        {IMPORT.RESULT.IMPORT_ANOTHER}
      </Button>
      {success && insurancePlans.length > 0 && (
        <>
          <Button
            type="primary--outlined"
            onClick={() => downloadParamsTemplate(insurancePlans)}
          >
            <MdOutlineDownload size={20} />
            {IMPORT.RESULT.DOWNLOAD_PARAMS_TEMPLATE}
          </Button>
          <Button
            type="primary--outlined"
            onClick={() => downloadDocsTemplate(insurancePlans)}
          >
            <MdOutlineDownload size={20} />
            {IMPORT.RESULT.DOWNLOAD_DOCS_TEMPLATE}
          </Button>
        </>
      )}
      <Button type="primary" onClick={onFinish}>
        <MdOutlineExplore size={20} />
        {IMPORT.RESULT.VIEW_PLANS}
      </Button>
    </>
  );

  return (
    <StepCard actions={resultActions}>
      <div className="pricers-import__result-container">
        <div
          className={classNames("pricers-import__result-icon", {
            "pricers-import__result-icon--success": success,
            "pricers-import__result-icon--failure": !success,
          })}
        >
          {success ? (
            <MdOutlineCheckCircle size={48} color={COLORS.C_SUCCESS} />
          ) : (
            <MdOutlineError size={48} color={COLORS.C_DANGER} />
          )}
        </div>
        <h2>
          {success ? IMPORT.RESULT.SUCCESS_TITLE : IMPORT.RESULT.FAILURE_TITLE}
        </h2>
        {success && strategy && (
          <p className="pricers-import__result-strategy">
            {IMPORT.RESULT.STRATEGY}:{" "}
            {get(IMPORT, `ANALYSIS.STRATEGIES.${strategy}.LABEL`, strategy)}
          </p>
        )}
      </div>

      {!success && errors.length > 0 && (
        <div className="pricers-import__error-section">
          <h4 className="pricers-import__error-title">
            {IMPORT.RESULT.ERROR_DETAILS}
          </h4>
          <div className="pricers-import__error-list">
            {errors.map((err, i) => (
              <div key={i} className="pricers-import__error-item">
                {resolveError(err, IMPORT.ERROR_KEYS)}
              </div>
            ))}
          </div>
        </div>
      )}

      {success && (
        <>
          <div className="grid--3 gap-16 mb-16">
            <div className="pricers-import__result-stat pricers-import__result-stat--created">
              <div className="pricers-import__result-stat-value pricers-import__result-stat-value--created">
                {created}
              </div>
              <div className="pricers-import__stat-label">
                {IMPORT.RESULT.CREATED}
              </div>
            </div>
            <div className="pricers-import__result-stat pricers-import__result-stat--updated">
              <div className="pricers-import__result-stat-value pricers-import__result-stat-value--updated">
                {updated}
              </div>
              <div className="pricers-import__stat-label">
                {IMPORT.RESULT.UPDATED}
              </div>
            </div>
            <div className="pricers-import__result-stat pricers-import__result-stat--skipped">
              <div className="pricers-import__result-stat-value pricers-import__result-stat-value--skipped">
                {skipped}
              </div>
              <div className="pricers-import__stat-label">
                {IMPORT.RESULT.SKIPPED}
              </div>
            </div>
          </div>

          <div className="pricers-import__result-footer">
            <span>
              {totalRows} {IMPORT.RESULT.ROWS_PROCESSED}
            </span>
          </div>
        </>
      )}
    </StepCard>
  );
};

export default StepResult;
